import { Button, Chip, MuiAlert, Paper, Spin, Typography, tryParseJSONObject, useRenderer } from '@fox/atoms';
import { getComponentExtension, sendUpdate, useSubscribeTopicWithHandler } from '@fox/extensions';
import { Customer, DirectDebit, PaymentAddProps, PaymentMethod, ProviderSelection, paymentExtension } from '@fox/finance';
import { Flex, FlexColumnSpaced, FlexRowSpaced, Padded } from '@fox/layout';
import { useMediaQuery } from '@mui/material';
import { useState } from 'react';
import { useContentContext } from '../../../content-context';
import { useFinancialContext } from '../../../financial-config-context';
import { FINANCIAL_DATA_FETCHED, REFETCH_FINANCIAL } from '../../../topics';
import { useUpdateSubscriptionPaymentMethodMutation } from '../../mutation-hooks/mutation-implementations';
import { SwiftFoxSubscription } from '../contracts';
import { convertMemberPortalToSfMethodEnumValue } from '../shared';
import { DeskTopOptions } from './desktop-options';
import { MobileOptions } from './mobile-options';
import { SubscriptionSelector } from './subscription-selector';

export function DirectDebitDisplay({ debit, meta }: { debit: DirectDebit; meta: Record<string, unknown> }) {
	const { getDirectDebit } = useFinancialContext();
	const useLinkedOrganisationData = meta?.['useLinkedOrganisationData'] === true;
	const directDebit = getDirectDebit(debit.id as string, useLinkedOrganisationData);
	const { account, accountName, bsb, id, inUse } = directDebit;
	const render = useRenderer();
	useSubscribeTopicWithHandler(FINANCIAL_DATA_FETCHED, () => render());

	return (
		<Paper style={{ width: '100%' }} key={id}>
			<Padded>
				<FlexColumnSpaced>
					<FlexRowSpaced style={{ justifyContent: 'space-between' }}>
						<Typography style={{ fontWeight: 'bold' }}>{accountName}</Typography>
						{inUse ? <Chip sx={{ fontSize: '0.5rem', height: '20px' }} label="In use" color="warning" /> : null}
					</FlexRowSpaced>
					<FlexRowSpaced style={{ justifyContent: 'space-between' }}>
						<Typography>
							<span style={{ fontWeight: 'bold' }}>BSB:</span> {bsb}
						</Typography>
						<Typography>
							<span style={{ fontWeight: 'bold' }}>Account:</span> {account}
						</Typography>
					</FlexRowSpaced>
				</FlexColumnSpaced>
			</Padded>
		</Paper>
	);
}

export function DirectDebitDisplayWithOptions({
	debit,
	provider,
	useLinkedOrganisationData,
	allowedPlanCategories
}: {
	debit: DirectDebit;
	provider: ProviderSelection;
	useLinkedOrganisationData: boolean;
	allowedPlanCategories?: string[];
}) {
	const isMobile = useMediaQuery('(max-width: 800px)');
	const { getSubscriptions, getDirectDebit } = useFinancialContext();
	const subscriptions = getSubscriptions(useLinkedOrganisationData, allowedPlanCategories);
	const directDebit = getDirectDebit(debit.id as string, useLinkedOrganisationData);
	const render = useRenderer();
	useSubscribeTopicWithHandler(FINANCIAL_DATA_FETCHED, () => render());

	const { account, accountName, bsb, id, inUse } = directDebit;

	const { requestToken, contentId } = useContentContext();
	const {
		error: updateSubscriptionError,
		loading: updateSubscriptionLoading,
		mutate: updateSubscription
	} = useUpdateSubscriptionPaymentMethodMutation({ requestToken, topics: [REFETCH_FINANCIAL] });

	const update = async (selectedSubscriptionIds: string[]) => {
		for (const subscriptionId of selectedSubscriptionIds) {
			await updateSubscription({
				id: subscriptionId,
				contentId,
				method: provider.method,
				provider: provider.provider,
				directDebitId: id as string
			});
		}
	};

	const subscriptionsUsingThisDirectDebit = subscriptions.filter(s => s.directDebitId === id);
	return (
		<Paper style={{ width: '100%' }} key={id}>
			<Padded>
				<FlexColumnSpaced>
					<FlexRowSpaced style={{ justifyContent: 'space-between' }}>
						<Typography style={{ fontWeight: 'bold' }}>{accountName}</Typography>
						{inUse ? <Chip sx={{ fontSize: '0.5rem', height: '20px' }} label="In use" color="warning" /> : null}
					</FlexRowSpaced>
					<FlexRowSpaced style={{ justifyContent: 'space-between' }}>
						<Typography>
							<span style={{ fontWeight: 'bold' }}>BSB:</span> {bsb}
						</Typography>
						<Typography>
							<span style={{ fontWeight: 'bold' }}>Account:</span> {account}
						</Typography>
					</FlexRowSpaced>
					{inUse ? (
						<FlexRowSpaced style={{ flexWrap: 'wrap' }}>
							<span style={{ fontWeight: 'bold', textWrap: 'nowrap' }}>Used by:</span>
							{subscriptionsUsingThisDirectDebit.map((s, i) => (
								<Typography key={s.id} style={{ textWrap: 'nowrap', fontStyle: 'italic' }}>
									{s.fee.plan.name}
									{i < subscriptionsUsingThisDirectDebit.length - 1 ? ', ' : ''}
								</Typography>
							))}
						</FlexRowSpaced>
					) : null}

					<Flex style={{ justifyContent: 'flex-start' }}>
						{isMobile ? (
							<MobileOptions
								id={id}
								paymentMethod={PaymentMethod.DirectDebit}
								updateError={updateSubscriptionError}
								updateLoading={updateSubscriptionLoading}
								update={update}
								useLinkedOrganisationData={useLinkedOrganisationData}
								allowedPlanCategories={allowedPlanCategories}
							/>
						) : (
							<DeskTopOptions
								id={id}
								paymentMethod={PaymentMethod.DirectDebit}
								updateError={updateSubscriptionError}
								updateLoading={updateSubscriptionLoading}
								update={update}
								useLinkedOrganisationData={useLinkedOrganisationData}
								allowedPlanCategories={allowedPlanCategories}
							/>
						)}
					</Flex>
				</FlexColumnSpaced>
			</Padded>
		</Paper>
	);
}

export function DirectDebits({
	provider,
	showProviderName,
	useLinkedOrganisationData,
	allowedPlanCategories,
	subscriptions,
	customer
}: {
	provider: ProviderSelection;
	showProviderName: boolean;
	useLinkedOrganisationData: boolean;
	subscriptions: SwiftFoxSubscription[];
	customer: Customer[];
	allowedPlanCategories?: string[];
}) {
	const [isOpened, setIsOpened] = useState<boolean>(false);
	const [selectedSubscriptionIds, setSelectedSubscriptionIds] = useState<string[]>(
		subscriptions?.length === 1 ? [subscriptions[0].id] : []
	);
	const { requestToken, contentId } = useContentContext();
	const {
		error: updateSubscriptionError,
		loading: updateSubscriptionLoading,
		mutate: updateSubscription
	} = useUpdateSubscriptionPaymentMethodMutation({ requestToken });
	const PaymentAdd = getComponentExtension<PaymentAddProps>(
		paymentExtension(provider.providerExtension, convertMemberPortalToSfMethodEnumValue(provider.method), 'add')
	);

	if (!PaymentAdd || !PaymentAdd) return null;

	return (
		<FlexColumnSpaced key={`${provider.name}_${provider.method}`}>
			{customer
				.flatMap(c => c.directDebits)
				?.filter(c => {
					return c.provider === provider.provider;
				})
				?.map(pm => {
					return (
						<DirectDebitDisplayWithOptions
							key={pm.id}
							debit={pm}
							provider={provider}
							useLinkedOrganisationData={useLinkedOrganisationData}
							allowedPlanCategories={allowedPlanCategories}
						/>
					);
				})}
			{updateSubscriptionError ? <MuiAlert severity="error">{updateSubscriptionError}</MuiAlert> : null}
			{isOpened ? (
				<Spin spinning={updateSubscriptionLoading}>
					<PaymentAdd
						onSuccess={async (id: string) => {
							for (const subscriptionId of selectedSubscriptionIds) {
								await updateSubscription({
									id: subscriptionId,
									contentId,
									method: provider.method,
									provider: provider.provider,
									directDebitId: id
								});
							}
							sendUpdate(REFETCH_FINANCIAL);
							setIsOpened(false);
						}}
						onCancel={() => setIsOpened(false)}
						provider={provider}
						settings={tryParseJSONObject(provider.settings)}
						showSave
						meta={{
							useLinkedOrganisationData: useLinkedOrganisationData
						}}
					/>
					<SubscriptionSelector
						selectedSubscriptionIds={selectedSubscriptionIds}
						setSelectedSubscriptionIds={setSelectedSubscriptionIds}
						subscriptions={subscriptions}
					/>
				</Spin>
			) : (
				<Button variant="tertiary" color="secondary" startIconName="Add" onClick={() => setIsOpened(true)}>
					{showProviderName ? `Add Direct Debit - ${provider.name}` : 'Add Direct Debit'}
				</Button>
			)}
		</FlexColumnSpaced>
	);
}

export function DirectDebitList({
	provider,
	showProviderName,
	useLinkedOrganisationData,
	allowedPlanCategories
}: {
	provider: ProviderSelection;
	showProviderName: boolean;
	useLinkedOrganisationData: boolean;
	allowedPlanCategories?: string[];
}) {
	const { getCustomers, getSubscriptions } = useFinancialContext();
	const customer = getCustomers(useLinkedOrganisationData);
	const subscriptions = getSubscriptions(useLinkedOrganisationData, allowedPlanCategories);
	const render = useRenderer();
	useSubscribeTopicWithHandler(FINANCIAL_DATA_FETCHED, () => render());
	return (
		<DirectDebits
			key={subscriptions.length}
			provider={provider}
			showProviderName={showProviderName}
			useLinkedOrganisationData={useLinkedOrganisationData}
			allowedPlanCategories={allowedPlanCategories}
			customer={customer}
			subscriptions={subscriptions}
		/>
	);
}
